import { useState, useCallback } from 'react'
import type { ProcedureCode } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  btnSecondary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { ProcedureCodeEditModal } from './ProcedureCodeEditModal'
import { BulkImportModal } from '../../components/BulkImportModal'
import { getAuthToken } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

export function ProcedureCodeListPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<ProcedureCode>({
    path: '/procedure-codes',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('procedure-codes')

  const columns: readonly Column<ProcedureCode>[] = [
    { key: 'code', header: 'CPT', sortable: true, filterable: true, render: (p) => <strong>{p.code}</strong> },
    { key: 'desc', header: 'Description', filterable: true, render: (p) => p.description },
    { key: 'mods', header: 'Modifiers', render: (p) => (p.modifiers.length ? p.modifiers.join(', ') : '--') },
    { key: 'fee', header: 'Default Fee', sortable: true, render: (p) => (p.defaultFee ? `$${p.defaultFee}` : '--') },
    {
      key: 'isSystem',
      header: 'System',
      render: (p) => (p.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    { key: 'status', header: 'Status', sortable: true, render: (p) => (p.isActive ? 'Active' : 'Archived') },
  ]

  const handleExportCsv = useCallback(() => {
    const params = new URLSearchParams({ format: 'csv' })
    if (debouncedSearch) params.set('search', debouncedSearch)
    const url = `/api/bulk/procedure_code/export?${params.toString()}`
    const token = getAuthToken()
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (res) => {
      if (!res.ok) return
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const filename = match ? match[1] : `procedure_code-${new Date().toISOString().slice(0, 10)}.csv`
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.click()
      URL.revokeObjectURL(objectUrl)
    })
  }, [debouncedSearch])

  return (
    <div>
      <PageHeader
        title="Procedure Codes (CPT)"
        subtitle={`${total} total`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelectedId(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Code
            </button>
            {user?.role !== 'tech' && (
              <button type="button" onClick={() => setImportModalOpen(true)} style={btnSecondary}>
                Import CSV
              </button>
            )}
            <button type="button" onClick={handleExportCsv} style={btnSecondary}>
              Export CSV
            </button>
          </div>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(p) => {
              setSelectedId(p.id)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No procedure codes yet. Add the codes your lab bills."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
      <ProcedureCodeEditModal
        codeId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />

      <BulkImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="procedure_code"
        entityLabel="Procedure Codes"
        onSuccess={refresh}
      />
    </div>
  )
}
